import { ReferenceField, FunctionField, useRecordContext } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { EventType, Event } from '../../types';
import { SensorStatusDisplay } from './SensorStatusDisplay';

const PrintPhotoContent = ({ eventType, record }: { record?: Event; eventType: EventType }) => {
  if (!record?.rawContent) return null;
  if (eventType.name !== 'print-photo') return null;

  try {
    const rawData = JSON.parse(record.rawContent);
    const {
      cnc = {},
      side,
      headerPrintings,
      lastHeaderPrintingClean,
      isDoorOpened,
      disableScratchDetector,
      zOffset,
      sensorStatus = {},
    } = rawData?.fields || {};
    const gcodePosition = cnc?.gcodePosition;
    const position = cnc?.position;
    const temperature = cnc?.temperature;
    const { funnel, pellets, up } = sensorStatus;

    if (
      !gcodePosition &&
      !position &&
      !temperature &&
      side === undefined &&
      headerPrintings === undefined &&
      lastHeaderPrintingClean === undefined &&
      isDoorOpened === undefined &&
      disableScratchDetector === undefined &&
      zOffset === undefined &&
      !funnel &&
      !pellets &&
      !up
    )
      return null;

    return (
      <Box sx={{ pl: 2, pb: 2 }}>
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          {gcodePosition !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                G-Code Position
              </Typography>
              <Typography variant="body2">
                <strong>{parseFloat(gcodePosition).toFixed(4)} mm</strong>
              </Typography>
            </Box>
          )}
          {position && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Position
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {position.X !== undefined && (
                  <Typography variant="body2">
                    <strong>X:</strong> {parseFloat(position.X).toFixed(2)} mm
                  </Typography>
                )}
                {position.Y !== undefined && (
                  <Typography variant="body2">
                    <strong>Y:</strong> {parseFloat(position.Y).toFixed(2)} mm
                  </Typography>
                )}
                {position.Z !== undefined && (
                  <Typography variant="body2">
                    <strong>Z:</strong> {parseFloat(position.Z).toFixed(2)} mm
                  </Typography>
                )}
                {position.E !== undefined && (
                  <Typography variant="body2">
                    <strong>E:</strong> {parseFloat(position.E).toFixed(3)} mm
                  </Typography>
                )}
                {position.extruderOffset !== undefined && (
                  <Typography variant="body2">
                    <strong>Extruder Offset:</strong> {position.extruderOffset}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {temperature && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Temperature
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {temperature.T0 !== undefined && (
                  <Typography variant="body2">
                    <strong>T0:</strong>{' '}
                    <Box
                      component="span"
                      sx={{
                        color: 'info.main',
                        fontWeight: 600,
                      }}
                    >
                      {parseFloat(temperature.T0).toFixed(1)}°C
                    </Box>
                  </Typography>
                )}
                {temperature.T1 !== undefined && (
                  <Typography variant="body2">
                    <strong>T1:</strong>{' '}
                    <Box
                      component="span"
                      sx={{
                        color: 'info.main',
                        fontWeight: 600,
                      }}
                    >
                      {parseFloat(temperature.T1).toFixed(1)}°C
                    </Box>
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          <SensorStatusDisplay funnel={funnel} pellets={pellets} up={up} />
          {side !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Side
              </Typography>
              <Typography variant="body2">
                <strong>{side}</strong>
              </Typography>
            </Box>
          )}
          {headerPrintings !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Header Printings
              </Typography>
              <Typography variant="body2">
                <strong>{headerPrintings}</strong>
              </Typography>
            </Box>
          )}
          {lastHeaderPrintingClean !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Last Header Printing Clean
              </Typography>
              <Typography variant="body2">
                <strong>{lastHeaderPrintingClean}</strong>
              </Typography>
            </Box>
          )}
          {zOffset !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Z Offset
              </Typography>
              <Typography variant="body2">
                <strong>{parseFloat(zOffset).toFixed(4)} mm</strong>
              </Typography>
            </Box>
          )}
          {isDoorOpened !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Door Status
              </Typography>
              <Typography variant="body2">
                <Box
                  component="span"
                  sx={{
                    color: isDoorOpened === 'true' ? 'warning.main' : 'success.main',
                    fontWeight: 600,
                  }}
                >
                  {isDoorOpened === 'true' ? 'Opened' : 'Closed'}
                </Box>
              </Typography>
            </Box>
          )}
          {disableScratchDetector !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Scratch Detector
              </Typography>
              <Typography variant="body2">
                <Box
                  component="span"
                  sx={{
                    color: disableScratchDetector === 'true' ? 'error.main' : 'success.main',
                    fontWeight: 600,
                  }}
                >
                  {disableScratchDetector === 'true' ? 'Disabled' : 'Enabled'}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  } catch {
    // Error parsing rawContent, silently ignore
    return null;
  }
};

export const PrintPhoto = () => {
  const record = useRecordContext<Event>();
  return (
    <ReferenceField source="eventTypeId" reference="event-types" link={false}>
      <FunctionField
        render={(eventType: EventType) => (
          <PrintPhotoContent record={record} eventType={eventType} />
        )}
      />
    </ReferenceField>
  );
};
